import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Api, ApiV2, Code } from '@.api';
import { AccessObjectTypeEnum, UserRequestType, UserResource } from '@.api/methods';
import { useDictionaries, useForm, useObjectEffect } from '@.hooks';
import { getUserRoleDictionaryByRoleEnum } from '@/Lib';
import { LinkHelper } from '@/Services';
import { DictionaryOptionResource } from '@/Services/api/methods/v2/Dictionary/response';
import { StatisticObjectEnum } from '@/Services/StatisticPermissionService';
import { DictionaryList } from '@/Types';
import { DictionaryResponseKnown } from '@/Types/api/Dictionaries';
import { UserRoleEnum } from '@/Types/api/User';

import { mapUserResponseTypeToUserData } from './mapUserResponseTypeToUserData';
import { StoreGroup, UsersData } from './types';
import { validateOptions } from './validate';

export const useUserFormAction = (isEdit: boolean, userId?: number, initialResponseUserData?: UserResource) => {
    const [dictionaries, loadDictionaries] = useDictionaries();

    const [groups, setGroups] = useState<StoreGroup[]>([]);

    const [storeGroupsForServiceProvider, setStoreGroupsForServiceProvider] = useState<DictionaryOptionResource[]>([]);

    const [field, form] = useForm<UsersData>();

    const navigate = useNavigate();

    const loadFormData = useCallback((userId: number, dictionaries: Partial<DictionaryResponseKnown>) => {
        Api.users()
            .getById(userId)
            .onSuccess((res) => {
                const formUserData = mapUserResponseTypeToUserData(res.data, dictionaries.stores);

                form.set.values(formUserData);
            });
    }, []);

    const fetchFormAction = () => {
        form.reset.errors();

        const serviceProviderRole = getUserRoleDictionaryByRoleEnum(dictionaries?.roles || [], UserRoleEnum.SERVICE_PROVIDER);

        const isServiceProviderRole = serviceProviderRole?.id === form.state.role_id?.value;

        form.ifValid((data) => {
            const requestUserData: UserRequestType = {
                ...data,
                store_groups_ids: data.is_company_admin ? [] : data.store_groups_ids ?? [],
            };

            const statisticObjectType = data.statistic_object_type;
            let statisticObjectValue: number | undefined;

            if (statisticObjectType === StatisticObjectEnum.COMPANY) {
                statisticObjectValue = data.company_id;
            } else if (statisticObjectType === StatisticObjectEnum.STORE_GROUP) {
                statisticObjectValue = data.statistic_object_store_group_id;
            } else if (statisticObjectType === StatisticObjectEnum.STORE) {
                statisticObjectValue = data.statistic_object_store_id;
            }

            const accessObjectType = data.access_object_type;
            let accessObjectValue: number | undefined;

            if (accessObjectType === AccessObjectTypeEnum.STORE_GROUP) {
                accessObjectValue = data.access_object_store_group_id;
            }

            if (accessObjectType === AccessObjectTypeEnum.STORE) {
                accessObjectValue = data.access_object_store_id;
            }

            if (statisticObjectValue) {
                requestUserData.statistic_object = {
                    type: statisticObjectType as StatisticObjectEnum,
                    id: statisticObjectValue,
                };
            }

            if (accessObjectValue) {
                requestUserData.access_object = {
                    type: accessObjectType as AccessObjectTypeEnum,
                    id: accessObjectValue,
                };
            }

            if (isEdit) {
                Api.users()
                    .updateById(requestUserData, Number(userId))
                    .onSuccess(() => navigate(LinkHelper.settingsUsers))
                    .onValidationError((res) => form.set.errors(res.errors));
            } else {
                Api.users()
                    .create(requestUserData)
                    .onSuccess(() => {
                        navigate(LinkHelper.settingsUsers);
                    })
                    .onValidationError((res) => form.set.errors(res.errors));
            }
        }, validateOptions(isEdit, isServiceProviderRole));
    };

    useEffect(() => {
        const dictionaries: DictionaryList = ['Companies', 'Roles', 'UserStatuses', 'StoreGroups', 'Stores'];

        if (isEdit) {
            dictionaries.push('StoreGroups');
        }

        loadDictionaries(dictionaries);
    }, [isEdit, loadDictionaries]);

    useObjectEffect(() => {
        if (form.state.company_id?.value) {
            Api.store_groups()
                .getAll({ company_id: form.state.company_id.value })
                .onSuccess((res) => {
                    const { store_groups } = res.data;

                    const storeGroupIds = store_groups.map((group) => group.id);

                    setGroups(store_groups);

                    const stateStoreGroupIds = form.state.store_groups_ids?.value;

                    if (stateStoreGroupIds && !stateStoreGroupIds.every((id) => storeGroupIds.includes(id))) {
                        field('store_groups_ids').value([]);
                    }
                });
        }
    }, [form.state.company_id]);

    /**
     * Load initial form data state
     */
    useEffect(() => {
        if (userId && dictionaries) {
            if (initialResponseUserData) {
                form.set.values(mapUserResponseTypeToUserData(initialResponseUserData, dictionaries.stores));
            } else if (isEdit) {
                loadFormData(Number(userId), dictionaries);
            }
        }
    }, [dictionaries, loadFormData, userId, isEdit, initialResponseUserData]);

    useEffect(() => {
        const loadStoreGroupsForServiceProvider = async () => {
            const response = await ApiV2.serviceProviderDictionaries().availableClustersForUserForm(userId);

            if (response.code === Code.Success) {
                setStoreGroupsForServiceProvider(response.data.options);
                return;
            }
        };

        loadStoreGroupsForServiceProvider();
    }, [setStoreGroupsForServiceProvider, userId]);

    return {
        form,
        dictionaries,
        groups,
        storeGroupsForServiceProvider,
        field,
        fetchFormAction,
    };
};
